// app/javascript/controllers/offer_controller.js
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["timer", "skeleton", "content", "image"];

  connect() {
    if (this.hasTimerTarget) {
      this.updateTimer();
      this.interval = setInterval(() => this.updateTimer(), 60000);
    }

    if (this.hasImageTarget) {
      if (this.imageTarget.complete && this.imageTarget.naturalHeight !== 0) {
        this.showContent();
      } else {
        this.imageTarget.addEventListener("load", () => this.showContent());
        this.imageTarget.addEventListener("error", () => this.showContent());
        this.skeletonTimeout = setTimeout(() => this.showContent(), 4000);
      }
    } else {
      this.showContent();
    }
  }

  disconnect() {
    if (this.interval) {
      clearInterval(this.interval);
    }
    if (this.skeletonTimeout) {
      clearTimeout(this.skeletonTimeout);
    }
  }

  updateTimer() {
    if (!this.hasTimerTarget) {
      console.warn("Timer target missing, skipping updateTimer");
      return;
    }

    const expiresAt = new Date(this.element.dataset.expiresAt);
    const now = new Date();
    const timeLeft = expiresAt - now;

    if (timeLeft <= 0) {
      this.timerTarget.textContent = this.t("offer_expired");
      this.timerTarget.classList.replace("bg-gradient-to-r", "bg-red-500");
      this.timerTarget.classList.remove("from-orange-500", "to-orange-600", "animate-pulse-slow");
      return;
    }

    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));

    if (days >= 1) {
      this.timerTarget.textContent = `${days}d ${hours}h ${this.t("left")}`;
    } else {
      this.timerTarget.textContent = `${hours}h ${minutes}m ${this.t("left")}`;
    }
  }

  showContent() {
    if (this.hasSkeletonTarget) {
      this.skeletonTarget.classList.add("hidden");
    } else {
      console.warn("Skeleton target missing");
    }

    if (this.hasContentTarget && this.contentTargets.length > 0) {
      this.contentTargets.forEach((content) => {
        content.classList.remove("hidden");
        content.classList.add("loaded");
      });
    } else {
      console.warn("Content target missing or empty", this.contentTargets);
    }

    if (this.hasImageTarget) {
      this.imageTarget.classList.remove("hidden");
      this.imageTarget.classList.add("loaded");
    } else {
      console.warn("Image target missing");
    }

    this.trackImageDisplay();
  }

  trackImageDisplay() {
    const offerId = this.element.dataset.offerId;
    const offerTitle = this.getOfferTitle();

    if (window.gtag) {
      gtag("event", "image_displayed", {
        offer_id: offerId,
        offer_title: offerTitle,
        event_category: "Offer",
        event_label: `Image Displayed Offer ${offerId}`
      });
    }

    fetch("/track_image_display", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name=csrf-token]").content
      },
      body: JSON.stringify({ offer_id: offerId, offer_title: offerTitle })
    })
      .catch((error) => console.error("Image display tracking error:", error));
  }

  trackView(event) {
    const offerId = this.element.dataset.offerId;
    const offerTitle = this.getOfferTitle();

    if (window.gtag) {
      gtag("event", "view_offer", {
        offer_id: offerId,
        offer_title: offerTitle,
        event_category: "Offer",
        event_label: `View Offer ${offerId}`
      });
    }

    fetch("/track_offer_view", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name=csrf-token]").content
      },
      body: JSON.stringify({ offer_id: offerId, offer_title: offerTitle })
    })
      .catch((error) => console.error("View tracking error:", error));
  }

  trackCoupon(event) {
    const offerId = this.element.dataset.offerId;
    if (window.gtag) {
      gtag("event", "coupon_claimed", {
        offer_id: offerId,
        offer_title: this.getOfferTitle(),
        event_category: "Offer",
        event_label: `Coupon Claimed Offer ${offerId}`
      });
    }
  }

  getOfferTitle() {
    if (!this.hasContentTarget || this.contentTargets.length === 0) {
      console.warn("No content targets found for title");
      return this.element.dataset.offerTitle || "Unknown";
    }

    const titleElement = this.contentTargets[0].querySelector("h3, h2");
    return titleElement?.textContent.trim() || this.element.dataset.offerTitle || "Unknown";
  }

  t(key) {
    try {
      const data = JSON.parse(document.getElementById("i18n-js")?.textContent || "{}");
      return data?.offer?.[key] || key;
    } catch {
      return key;
    }
  }
}
